import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  type BarRectangleItem,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { authFetchJson } from "@/lib/api";
import { csvFilename, downloadCsv, toCsv } from "@/lib/csv";
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_LEGEND,
  CHART_TOOLTIP,
  CS_COLORS,
  SERIES_PALETTE,
} from "@/lib/chartTheme";
import { useCountUp } from "@/lib/useCountUp";
import { loadSavedViews, newViewId, saveSavedViews, type SavedView } from "@/lib/savedViews";
import {
  departmentsForOrganization,
  type OverviewDepartmentOption,
  type OverviewOrganizationOption,
} from "./adminOverviewScope";

// ── Types ───────────────────────────────────────────────────────────

type ScopeNode = { id: string; name: string };

type OverviewKpis = {
  total_users: number;
  dau_today: number;
  mau_30d: number;
  recordings_window: number;
  recordings_prev_window: number;
  dictations_window: number;
  dictations_prev_window: number;
  conversations_window: number;
  conversations_prev_window: number;
  audio_minutes_window: number;
  audio_minutes_prev_window: number;
  dictation_audio_minutes_window: number;
  dictation_audio_minutes_prev_window: number;
  conversation_audio_minutes_window: number;
  conversation_audio_minutes_prev_window: number;
  failed_recordings_window: number;
  errors_window: number;
  error_rate_window: number;
  hours_saved_total: number;
  cost_this_month_usd: number | null;
};

type DailyPoint = {
  date: string;
  recordings: number;
  dictations: number;
  conversations: number;
  audio_minutes: number;
  dictation_audio_minutes: number;
  conversation_audio_minutes: number;
  failed_recordings: number;
  recordings_cumulative: number;
  audio_minutes_cumulative: number;
  errors: number;
  active_users: number;
  new_users: number;
  users_cumulative: number;
};

type TopItem = {
  id: string;
  label: string;
  recordings: number;
  dictations: number;
  conversations: number;
  audio_minutes: number;
  dictation_audio_minutes: number;
  conversation_audio_minutes: number;
  total: number;
};
type TopLevel = "organisations" | "departments" | "associations" | "users";

type TopUserRecordings = { id: string; label: string; recordings: number };
type TopUserAudioMinutes = { id: string; label: string; audio_minutes: number };
type TopUserCost = { id: string; label: string; cost_usd: number };

type RecentError = { error_type: string; count: number };

type OverviewResponse = {
  scope: {
    org: ScopeNode | null;
    department: ScopeNode | null;
    association: ScopeNode | null;
  };
  window: { from: string; to: string; days: number };
  kpis: OverviewKpis;
  daily: DailyPoint[];
  distributions: {
    recordings_by_status: Record<string, number>;
    recordings_by_source: Record<string, number>;
    audio_minutes_by_status: Record<string, number>;
    audio_minutes_by_source: Record<string, number>;
  };
  top: { level: TopLevel; items: TopItem[]; audio_items: TopItem[] };
  top_users_by_recordings: TopUserRecordings[];
  top_users_by_audio_minutes: TopUserAudioMinutes[];
  top_users_by_cost: TopUserCost[] | null;
  operational: {
    recent_errors: RecentError[];
  };
};

type Preset = "7d" | "30d" | "90d" | "365d" | "custom";
type RangeState = { preset: Preset; from: string; to: string };
type ActivityMetric = "recordings" | "audio_minutes";

// ── Constants ───────────────────────────────────────────────────────

const STORAGE_KEY = "admin.overview.range";
const METRIC_STORAGE_KEY = "admin.overview.activityMetric";
const DASHBOARD_TIME_ZONE = "Europe/Brussels";

const PALETTE = SERIES_PALETTE;

const PRESETS: ReadonlyArray<{ key: Preset; label: string; days: number | null }> = [
  { key: "7d", label: "7 dagen", days: 7 },
  { key: "30d", label: "30 dagen", days: 30 },
  { key: "90d", label: "90 dagen", days: 90 },
  { key: "365d", label: "1 jaar", days: 365 },
  { key: "custom", label: "Aangepast", days: null },
];

const TOP_LEVEL_LABEL: Record<TopLevel, string> = {
  organisations: "Top organisaties",
  departments: "Top afdelingen",
  associations: "Top associaties",
  users: "Top gebruikers",
};

const RECORDING_STATUS_LABEL: Record<string, string> = {
  uploading: "Uploaden",
  uploaded: "Geüpload",
  transcribed: "Getranscribeerd",
  failed: "Mislukt",
  deleted: "Verwijderd",
  purged: "Gewist",
  unknown: "Onbekend",
};

const RECORDING_SOURCE_LABEL: Record<string, string> = {
  live: "Dictaten",
  ambient: "Gesprekken",
  unknown: "Onbekend",
};

// ── Helpers ─────────────────────────────────────────────────────────

function todayIso(): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DASHBOARD_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function isoMinusDays(days: number): string {
  const d = new Date(`${todayIso()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().slice(0, 10);
}

function loadRange(): RangeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as RangeState;
      if (parsed && parsed.preset && parsed.from && parsed.to) return parsed;
    }
  } catch {
    // fall through to default
  }
  return { preset: "30d", from: isoMinusDays(30), to: todayIso() };
}

function saveRange(range: RangeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
  } catch {
    // localStorage may be disabled — silently ignore
  }
}

function loadActivityMetric(): ActivityMetric {
  try {
    return localStorage.getItem(METRIC_STORAGE_KEY) === "audio_minutes"
      ? "audio_minutes"
      : "recordings";
  } catch {
    return "recordings";
  }
}

function saveActivityMetric(metric: ActivityMetric): void {
  try {
    localStorage.setItem(METRIC_STORAGE_KEY, metric);
  } catch {
    // localStorage may be disabled — silently ignore
  }
}

function fmtNumber(n: number): string {
  return n.toLocaleString("nl-BE");
}

function fmtCurrency(n: number): string {
  return n.toLocaleString("nl-BE", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function fmtMinutes(n: number): string {
  return `${n.toLocaleString("nl-BE", { maximumFractionDigits: 2 })} min`;
}

function fmtPercent(n: number): string {
  return (n * 100).toLocaleString("nl-BE", { maximumFractionDigits: 1 }) + "%";
}

function deltaPercent(curr: number, prev: number): number | null {
  if (prev === 0) return curr === 0 ? 0 : null; // null = "no baseline"
  return (curr - prev) / prev;
}

function fmtTickDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("nl-BE", {
    timeZone: DASHBOARD_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
  });
}

function fmtTooltipDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("nl-BE", {
    timeZone: DASHBOARD_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ── DateRangeControl ────────────────────────────────────────────────

function DateRangeControl({
  range,
  onChange,
}: {
  range: RangeState;
  onChange: (next: RangeState) => void;
}) {
  const today = todayIso();
  const setPreset = (key: Preset) => {
    if (key === "custom") {
      onChange({ ...range, preset: "custom" });
      return;
    }
    const days = PRESETS.find((p) => p.key === key)?.days ?? 30;
    onChange({ preset: key, from: isoMinusDays(days), to: today });
  };
  const setFrom = (v: string) => onChange({ ...range, preset: "custom", from: v });
  const setTo = (v: string) => onChange({ ...range, preset: "custom", to: v });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-1 rounded-full border border-[rgba(10,52,96,0.10)] bg-white p-1 shadow-[0_4px_12px_rgba(10,52,96,0.04)]">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPreset(p.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              range.preset === p.key
                ? "bg-[var(--cs-rose)] text-white shadow-[0_8px_18px_rgba(225,29,72,0.20)]"
                : "text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {range.preset === "custom" ? (
        <div className="flex items-center gap-1.5 rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-2 py-1 text-xs">
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => setFrom(e.target.value)}
            className="border-0 bg-transparent text-[var(--cs-navy)] focus:outline-none"
          />
          <span className="text-[var(--cs-ink-subtle)]">→</span>
          <input
            type="date"
            value={range.to}
            min={range.from}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            className="border-0 bg-transparent text-[var(--cs-navy)] focus:outline-none"
          />
        </div>
      ) : null}
    </div>
  );
}

// ── KPI Card ────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  delta,
  format = "number",
  spark,
  sparkColor,
}: {
  label: string;
  value: number | string | null;
  delta?: number | null;
  format?: "number" | "currency" | "percent" | "minutes";
  spark?: number[];
  sparkColor?: string;
}) {
  const numeric = typeof value === "number" ? value : 0;
  const animated = useCountUp(numeric);
  const display = typeof value === "number" ? animated : value;

  const formatted =
    display === null
      ? "—"
      : typeof display === "string"
      ? display
      : format === "currency"
      ? fmtCurrency(display)
      : format === "minutes"
      ? fmtMinutes(display)
      : format === "percent"
      ? fmtPercent(display)
      : fmtNumber(Math.round(display));

  let deltaNode: React.ReactNode = null;
  if (delta !== undefined && delta !== null) {
    const positive = delta >= 0;
    const abs = Math.abs(delta);
    deltaNode = (
      <span
        className={`cs-mono text-[10px] font-semibold ${
          positive ? "text-[var(--cs-emerald)]" : "text-[var(--cs-rose)]"
        }`}
      >
        {positive ? "▲" : "▼"} {fmtPercent(abs)}
      </span>
    );
  } else if (delta === null) {
    deltaNode = <span className="text-[10px] text-[var(--cs-ink-subtle)]">—</span>;
  }

  return (
    <div className="glass-card group relative overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] p-4 transition-all hover:-translate-y-[1px]">
      {spark && spark.length > 1 ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 opacity-60">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark.map((v, i) => ({ i, v }))} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Area
                type="monotone"
                dataKey="v"
                stroke={sparkColor ?? CS_COLORS.rose}
                fill={sparkColor ?? CS_COLORS.rose}
                fillOpacity={0.12}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
      <div className="relative">
        <p className="cs-caption">{label}</p>
        <div className="mt-1.5 flex items-baseline justify-between gap-2">
          <p className="cs-mono text-[26px] font-semibold leading-none text-[var(--cs-navy)]">{formatted}</p>
          {deltaNode}
        </div>
      </div>
    </div>
  );
}

// ── Chart cards ─────────────────────────────────────────────────────

function ChartCard({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="glass-card rounded-2xl border border-[rgba(10,52,96,0.10)] p-5">
      <div className="mb-4 flex items-center justify-between border-b border-[rgba(10,52,96,0.06)] pb-2">
        <h3 className="text-sm font-semibold tracking-tight text-[var(--cs-navy)]">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-48 items-center justify-center text-xs text-[var(--cs-ink-subtle)]">
      {message}
    </div>
  );
}

// ── Distributions: pie charts ───────────────────────────────────────

function DistributionPie({
  data,
  emptyMessage,
  valueFormatter = fmtNumber,
}: {
  data: { name: string; value: number }[];
  emptyMessage: string;
  valueFormatter?: (value: number) => string;
}) {
  if (data.length === 0 || data.every((d) => d.value === 0)) {
    return <EmptyState message={emptyMessage} />;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={50}
          outerRadius={85}
          paddingAngle={2}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={CHART_TOOLTIP.contentStyle}
          labelStyle={CHART_TOOLTIP.labelStyle}
          itemStyle={CHART_TOOLTIP.itemStyle}
          formatter={(value) => valueFormatter(Number(value))}
        />
        <Legend wrapperStyle={CHART_LEGEND.wrapperStyle} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Top list bar chart ──────────────────────────────────────────────

function TopList({
  items,
  onDrill,
  metric,
}: {
  items: TopItem[];
  onDrill: ((item: TopItem) => void) | null;
  metric: ActivityMetric;
}) {
  if (items.length === 0) {
    return <EmptyState message="Geen activiteit in deze periode." />;
  }
  const data = items.map((it) => ({
    label: it.label.length > 28 ? it.label.slice(0, 27) + "…" : it.label,
    dictations:
      metric === "audio_minutes" ? it.dictation_audio_minutes : it.dictations,
    conversations:
      metric === "audio_minutes" ? it.conversation_audio_minutes : it.conversations,
    id: it.id,
  }));
  const valueFormatter = metric === "audio_minutes" ? fmtMinutes : fmtNumber;
  const dictationLabel = metric === "audio_minutes" ? "Dictaatminuten" : "Dictaten";
  const conversationLabel = metric === "audio_minutes" ? "Gespreksminuten" : "Gesprekken";
  const height = Math.max(180, items.length * 28 + 40);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray={CHART_GRID.strokeDasharray} stroke={CHART_GRID.stroke} horizontal={false} />
        <XAxis type="number" tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
        <YAxis
          type="category"
          dataKey="label"
          tick={CHART_AXIS.tick}
          stroke={CHART_AXIS.stroke}
          width={140}
          interval={0}
        />
        <Tooltip
          contentStyle={CHART_TOOLTIP.contentStyle}
          labelStyle={CHART_TOOLTIP.labelStyle}
          itemStyle={CHART_TOOLTIP.itemStyle}
          cursor={{ fill: "rgba(10,52,96,0.06)" }}
          formatter={(v, key) => [
            valueFormatter(Number(v)),
            key === "dictations" ? dictationLabel : conversationLabel,
          ]}
          labelFormatter={(label) =>
            onDrill ? `${String(label)} — klik om in te zoomen` : label
          }
        />
        <Legend wrapperStyle={CHART_LEGEND.wrapperStyle} />
        <Bar
          dataKey="dictations"
          name={dictationLabel}
          fill={CS_COLORS.navy}
          stackId="a"
          onClick={
            onDrill
              ? (entry: BarRectangleItem) => {
                  const item = items.find((i) => i.id === entry.payload?.id);
                  if (item) onDrill(item);
                }
              : undefined
          }
          cursor={onDrill ? "pointer" : "default"}
        />
        <Bar
          dataKey="conversations"
          name={conversationLabel}
          fill={CS_COLORS.rose}
          stackId="a"
          onClick={
            onDrill
              ? (entry: BarRectangleItem) => {
                  const item = items.find((i) => i.id === entry.payload?.id);
                  if (item) onDrill(item);
                }
              : undefined
          }
          cursor={onDrill ? "pointer" : "default"}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Top users single-metric bar chart ───────────────────────────────

function TopUsersBar({
  items,
  metricKey,
  metricLabel,
  color,
  format,
  emptyMessage,
}: {
  items: { id: string; label: string; value: number }[];
  metricKey: string;
  metricLabel: string;
  color: string;
  format: "number" | "currency" | "minutes";
  emptyMessage: string;
}) {
  if (items.length === 0 || items.every((d) => d.value === 0)) {
    return <EmptyState message={emptyMessage} />;
  }
  const data = items.map((it) => ({
    label: it.label.length > 28 ? it.label.slice(0, 27) + "…" : it.label,
    [metricKey]: it.value,
    id: it.id,
  }));
  const height = Math.max(180, items.length * 28 + 40);
  const fmt = format === "currency" ? fmtCurrency : format === "minutes" ? fmtMinutes : fmtNumber;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray={CHART_GRID.strokeDasharray} stroke={CHART_GRID.stroke} horizontal={false} />
        <XAxis
          type="number"
          tick={CHART_AXIS.tick}
          stroke={CHART_AXIS.stroke}
          tickFormatter={(v: number) => fmt(v)}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={CHART_AXIS.tick}
          stroke={CHART_AXIS.stroke}
          width={160}
          interval={0}
        />
        <Tooltip
          contentStyle={CHART_TOOLTIP.contentStyle}
          labelStyle={CHART_TOOLTIP.labelStyle}
          itemStyle={CHART_TOOLTIP.itemStyle}
          cursor={{ fill: "rgba(10,52,96,0.06)" }}
          formatter={(v) => [fmt(Number(v)), metricLabel]}
        />
        <Bar dataKey={metricKey} name={metricLabel} fill={color} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Export menu ─────────────────────────────────────────────────────

function ExportMenu({ data }: { data: OverviewResponse }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const exportKpis = () => {
    const k = data.kpis;
    const csv = toCsv(
      [{
        from: data.window.from,
        to: data.window.to,
        ...k,
        cost_this_month_usd: k.cost_this_month_usd ?? "",
      }],
      [
        { key: "from", label: "Van" },
        { key: "to", label: "Tot" },
        { key: "total_users", label: "Gebruikers totaal" },
        { key: "dau_today", label: "Actieve gebruikers vandaag" },
        { key: "mau_30d", label: "MAU 30d" },
        { key: "recordings_window", label: "Opnames (periode)" },
        { key: "recordings_prev_window", label: "Opnames (vorige periode)" },
        { key: "dictations_window", label: "Dictaten (periode)" },
        { key: "dictations_prev_window", label: "Dictaten (vorige periode)" },
        { key: "conversations_window", label: "Gesprekken (periode)" },
        { key: "conversations_prev_window", label: "Gesprekken (vorige periode)" },
        { key: "audio_minutes_window", label: "Audiominuten (periode)" },
        { key: "audio_minutes_prev_window", label: "Audiominuten (vorige periode)" },
        { key: "dictation_audio_minutes_window", label: "Dictaatminuten (periode)" },
        { key: "dictation_audio_minutes_prev_window", label: "Dictaatminuten (vorige periode)" },
        { key: "conversation_audio_minutes_window", label: "Gespreksminuten (periode)" },
        { key: "conversation_audio_minutes_prev_window", label: "Gespreksminuten (vorige periode)" },
        { key: "failed_recordings_window", label: "Mislukte opnames (periode)" },
        { key: "hours_saved_total", label: "Uren bespaard (totaal)" },
        { key: "cost_this_month_usd", label: "Kosten deze maand (USD)" },
      ],
    );
    downloadCsv(csvFilename("kpis", data.window.from, data.window.to), csv);
    setOpen(false);
  };

  const exportDaily = () => {
    const csv = toCsv(data.daily, [
      { key: "date", label: "Datum" },
      { key: "recordings", label: "Opnames" },
      { key: "dictations", label: "Dictaten" },
      { key: "conversations", label: "Gesprekken" },
      { key: "audio_minutes", label: "Audiominuten" },
      { key: "dictation_audio_minutes", label: "Dictaatminuten" },
      { key: "conversation_audio_minutes", label: "Gespreksminuten" },
      { key: "failed_recordings", label: "Mislukte opnames" },
      { key: "recordings_cumulative", label: "Opnames cumulatief in periode" },
      { key: "audio_minutes_cumulative", label: "Audiominuten cumulatief in periode" },
      { key: "active_users", label: "Gebruikers met opname" },
      { key: "new_users", label: "Nieuwe gebruikers" },
      { key: "users_cumulative", label: "Gebruikers cumulatief" },
    ]);
    downloadCsv(csvFilename("dagreeks", data.window.from, data.window.to), csv);
    setOpen(false);
  };

  const exportDistributions = () => {
    const rows: { category: string; label: string; value: number }[] = [];
    for (const [k, v] of Object.entries(data.distributions.recordings_by_status)) {
      rows.push({ category: "recordings_by_status", label: k, value: v });
    }
    for (const [k, v] of Object.entries(data.distributions.recordings_by_source)) {
      rows.push({ category: "recordings_by_source", label: k, value: v });
    }
    for (const [k, v] of Object.entries(data.distributions.audio_minutes_by_status)) {
      rows.push({ category: "audio_minutes_by_status", label: k, value: v });
    }
    for (const [k, v] of Object.entries(data.distributions.audio_minutes_by_source)) {
      rows.push({ category: "audio_minutes_by_source", label: k, value: v });
    }
    for (const item of data.top.items) {
      rows.push({ category: `top_${data.top.level}`, label: item.label, value: item.total });
    }
    for (const item of data.top.audio_items) {
      rows.push({
        category: `top_${data.top.level}_audio_minutes`,
        label: item.label,
        value: item.audio_minutes,
      });
    }
    const csv = toCsv(rows, [
      { key: "category", label: "Categorie" },
      { key: "label", label: "Label" },
      { key: "value", label: "Waarde" },
    ]);
    downloadCsv(csvFilename("verdelingen", data.window.from, data.window.to), csv);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-[rgba(10,52,96,0.10)] bg-white px-4 py-1.5 text-xs font-medium text-[var(--cs-navy)] shadow-[0_8px_18px_rgba(10,52,96,0.06)] transition-all hover:-translate-y-[1px] hover:bg-[var(--cs-ivory)]"
      >
        Exporteer ▾
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white py-1 shadow-[0_24px_48px_rgba(10,52,96,0.14)]">
          <button
            type="button"
            onClick={exportKpis}
            className="block w-full px-3 py-1.5 text-left text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
          >
            KPIs (CSV)
          </button>
          <button
            type="button"
            onClick={exportDaily}
            className="block w-full px-3 py-1.5 text-left text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
          >
            Dagreeks (CSV)
          </button>
          <button
            type="button"
            onClick={exportDistributions}
            className="block w-full px-3 py-1.5 text-left text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
          >
            Verdelingen (CSV)
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Saved views menu ────────────────────────────────────────────────

function SavedViewsMenu({
  views,
  onApply,
  onSave,
  onDelete,
}: {
  views: SavedView[];
  onApply: (v: SavedView) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-[rgba(10,52,96,0.10)] bg-white px-4 py-1.5 text-xs font-medium text-[var(--cs-navy)] shadow-[0_8px_18px_rgba(10,52,96,0.06)] transition-all hover:-translate-y-[1px] hover:bg-[var(--cs-ivory)]"
      >
        Weergaven ▾
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white py-1 shadow-[0_24px_48px_rgba(10,52,96,0.14)]">
          <button
            type="button"
            onClick={() => {
              onSave();
              setOpen(false);
            }}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-[var(--cs-rose)] hover:bg-[var(--cs-ivory)]"
          >
            <span>Sla huidige weergave op</span>
            <span aria-hidden>＋</span>
          </button>
          {views.length > 0 ? (
            <div className="border-t border-[rgba(10,52,96,0.10)] py-1">
              {views.map((v) => (
                <div
                  key={v.id}
                  className="group flex items-center justify-between px-3 py-1.5 hover:bg-[var(--cs-ivory)]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onApply(v);
                      setOpen(false);
                    }}
                    className="flex-1 truncate text-left text-xs text-[var(--cs-navy)]"
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(v.id)}
                    aria-label={`Verwijder ${v.name}`}
                    className="ml-2 text-[var(--cs-ink-subtle)] opacity-0 transition-opacity hover:text-[var(--cs-rose)] group-hover:opacity-100"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="border-t border-[rgba(10,52,96,0.10)] px-3 py-2 text-[10px] italic text-[var(--cs-ink-muted)]">
              Geen opgeslagen weergaven.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Breadcrumb ──────────────────────────────────────────────────────

type Scope = { orgId: string | null; deptId: string | null; assocId: string | null };

const SCOPE_STORAGE_KEY = "admin.overview.scope";

function loadScope(isSuperuser: boolean, defaultOrgId: string | null): Scope {
  try {
    const raw = localStorage.getItem(SCOPE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Scope;
      if (parsed) {
        if (!isSuperuser) {
          const isOwnOrganization = parsed.orgId === defaultOrgId;
          return {
            orgId: defaultOrgId,
            deptId: isOwnOrganization ? parsed.deptId ?? null : null,
            assocId: isOwnOrganization ? parsed.assocId ?? null : null,
          };
        }
        return {
          orgId: parsed.orgId ?? null,
          deptId: parsed.deptId ?? null,
          assocId: parsed.assocId ?? null,
        };
      }
    }
  } catch {
    // fall through to default
  }
  return { orgId: isSuperuser ? null : defaultOrgId, deptId: null, assocId: null };
}

function saveScope(scope: Scope): void {
  try {
    localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // localStorage may be disabled — silently ignore
  }
}

function Breadcrumb({
  scope,
  scopeLabels,
  rootLabel,
  onJumpTo,
}: {
  scope: Scope;
  scopeLabels: { org: string | null; department: string | null; association: string | null };
  rootLabel: string;
  onJumpTo: (level: "root" | "org" | "department") => void;
}) {
  const chips: { label: string; level: "root" | "org" | "department" | "association"; clickable: boolean }[] = [];
  chips.push({ label: rootLabel, level: "root", clickable: scope.orgId !== null });
  if (scopeLabels.org) chips.push({ label: scopeLabels.org, level: "org", clickable: scope.deptId !== null });
  if (scopeLabels.department) chips.push({ label: scopeLabels.department, level: "department", clickable: scope.assocId !== null });
  if (scopeLabels.association) chips.push({ label: scopeLabels.association, level: "association", clickable: false });

  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-sm text-[rgba(10,52,96,0.78)]">
      {chips.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 ? <span aria-hidden className="text-[var(--cs-ink-subtle)]">›</span> : null}
          {c.clickable ? (
            <button
              type="button"
              onClick={() => onJumpTo(c.level === "association" ? "department" : c.level)}
              className="rounded-full px-2.5 py-0.5 font-medium text-[rgba(10,52,96,0.62)] transition-colors hover:bg-[var(--cs-ivory)] hover:text-[var(--cs-navy)]"
            >
              {c.label}
            </button>
          ) : (
            <span className="inline-flex items-center rounded-full border border-[rgba(225,29,72,0.20)] bg-[rgba(225,29,72,0.06)] px-2.5 py-0.5 font-semibold text-[var(--cs-rose)]">
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

function ScopeFilters({
  isSuperuser,
  scope,
  organizations,
  departments,
  loading,
  activityMetric,
  onOrganizationChange,
  onDepartmentChange,
  onActivityMetricChange,
}: {
  isSuperuser: boolean;
  scope: Scope;
  organizations: OverviewOrganizationOption[];
  departments: OverviewDepartmentOption[];
  loading: boolean;
  activityMetric: ActivityMetric;
  onOrganizationChange: (orgId: string | null) => void;
  onDepartmentChange: (deptId: string | null) => void;
  onActivityMetricChange: (metric: ActivityMetric) => void;
}) {
  const availableDepartments = departmentsForOrganization(departments, scope.orgId);

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white px-3 py-2 shadow-[0_4px_12px_rgba(10,52,96,0.04)]">
      <span className="cs-caption self-center pr-1">Filter op</span>
      <label className="min-w-52 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--cs-ink-muted)]">
        Ziekenhuis
        <select
          aria-label="Filter op ziekenhuis"
          value={scope.orgId ?? ""}
          disabled={loading || !isSuperuser}
          onChange={(event) => onOrganizationChange(event.target.value || null)}
          className="mt-1 block w-full rounded-lg border border-[rgba(10,52,96,0.16)] bg-white px-3 py-1.5 text-xs font-medium normal-case tracking-normal text-[var(--cs-navy)] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)] disabled:bg-[var(--cs-ivory)] disabled:text-[var(--cs-ink-muted)]"
        >
          {isSuperuser ? <option value="">Alle ziekenhuizen</option> : null}
          {organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      </label>
      <label className="min-w-52 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--cs-ink-muted)]">
        Afdeling
        <select
          aria-label="Filter op afdeling"
          value={scope.deptId ?? ""}
          disabled={loading || !scope.orgId}
          onChange={(event) => onDepartmentChange(event.target.value || null)}
          className="mt-1 block w-full rounded-lg border border-[rgba(10,52,96,0.16)] bg-white px-3 py-1.5 text-xs font-medium normal-case tracking-normal text-[var(--cs-navy)] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)] disabled:bg-[var(--cs-ivory)] disabled:text-[var(--cs-ink-muted)]"
        >
          <option value="">{scope.orgId ? "Alle afdelingen" : "Kies eerst een ziekenhuis"}</option>
          {availableDepartments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>
      </label>
      <label className="min-w-44 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--cs-ink-muted)]">
        Weergave
        <select
          aria-label="Kies activiteitsweergave"
          value={activityMetric}
          onChange={(event) =>
            onActivityMetricChange(
              event.target.value === "audio_minutes" ? "audio_minutes" : "recordings",
            )
          }
          className="mt-1 block w-full rounded-lg border border-[rgba(10,52,96,0.16)] bg-white px-3 py-1.5 text-xs font-medium normal-case tracking-normal text-[var(--cs-navy)] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
        >
          <option value="recordings">Aantallen</option>
          <option value="audio_minutes">Audiominuten</option>
        </select>
      </label>
    </div>
  );
}

// ── Main tab ────────────────────────────────────────────────────────

export function AdminOverviewTab({
  isSuperuser,
  defaultOrgId,
}: {
  isSuperuser: boolean;
  defaultOrgId: string | null;
}) {
  const [range, setRange] = useState<RangeState>(loadRange);
  const [activityMetric, setActivityMetric] = useState<ActivityMetric>(loadActivityMetric);
  const [scope, setScope] = useState<Scope>(() => loadScope(isSuperuser, defaultOrgId));
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews());
  const [organizations, setOrganizations] = useState<OverviewOrganizationOption[]>([]);
  const [departments, setDepartments] = useState<OverviewDepartmentOption[]>([]);
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(true);
  const [scopeOptionsError, setScopeOptionsError] = useState("");

  useEffect(() => saveRange(range), [range]);
  useEffect(() => saveActivityMetric(activityMetric), [activityMetric]);
  useEffect(() => saveScope(scope), [scope]);

  useEffect(() => {
    let cancelled = false;
    setScopeOptionsLoading(true);
    setScopeOptionsError("");
    void Promise.all([
      authFetchJson<{ organizations: OverviewOrganizationOption[] }>("/api/admin/organizations"),
      authFetchJson<{ departments: OverviewDepartmentOption[] }>("/api/admin/departments"),
    ])
      .then(([organizationResponse, departmentResponse]) => {
        if (cancelled) return;
        setOrganizations(organizationResponse.organizations ?? []);
        setDepartments(departmentResponse.departments ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setScopeOptionsError(
          err instanceof Error ? err.message : "Filters konden niet worden geladen.",
        );
      })
      .finally(() => {
        if (!cancelled) setScopeOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Strip legacy ?from=…&to=…&org_id=… query string that earlier versions wrote.
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    }
  }, []);

  const persistViews = (views: SavedView[]) => {
    setSavedViews(views);
    saveSavedViews(views);
  };

  const handleSaveCurrentView = () => {
    const name = window.prompt("Naam voor deze weergave:");
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const next: SavedView = {
      id: newViewId(),
      name: trimmed,
      range: { ...range },
      scope: { ...scope },
      activityMetric,
    };
    persistViews([...savedViews, next].slice(-20));
  };

  const handleApplyView = (view: SavedView) => {
    setRange(view.range as RangeState);
    setActivityMetric(view.activityMetric ?? "recordings");
    if (isSuperuser || view.scope.orgId === defaultOrgId) {
      setScope(view.scope);
    } else {
      setScope({ orgId: defaultOrgId, deptId: null, assocId: null });
    }
  };

  const handleDeleteView = (id: string) => {
    persistViews(savedViews.filter((v) => v.id !== id));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("from", range.from);
      params.set("to", range.to);
      if (scope.orgId) params.set("org_id", scope.orgId);
      if (scope.deptId) params.set("dept_id", scope.deptId);
      if (scope.assocId) params.set("assoc_id", scope.assocId);
      const res = await authFetchJson<OverviewResponse>(
        `/api/admin/overview?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fout bij laden.");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, scope.orgId, scope.deptId, scope.assocId]);

  useEffect(() => {
    void load();
  }, [load]);

  const drillInto = (item: TopItem) => {
    if (!data || item.id === "__unassigned__") return;
    const level = data.top.level;
    if (level === "organisations") setScope({ orgId: item.id, deptId: null, assocId: null });
    else if (level === "departments") setScope({ ...scope, deptId: item.id, assocId: null });
    else if (level === "associations") setScope({ ...scope, assocId: item.id });
    // users level is leaf — no drill
  };

  const jumpTo = (level: "root" | "org" | "department") => {
    if (level === "root") {
      if (isSuperuser) setScope({ orgId: null, deptId: null, assocId: null });
      else setScope({ orgId: defaultOrgId, deptId: null, assocId: null });
    } else if (level === "org") {
      setScope({ ...scope, deptId: null, assocId: null });
    } else if (level === "department") {
      setScope({ ...scope, assocId: null });
    }
  };

  const selectOrganization = (orgId: string | null) => {
    setScope({ orgId, deptId: null, assocId: null });
  };

  const selectDepartment = (deptId: string | null) => {
    setScope((current) => ({ ...current, deptId, assocId: null }));
  };

  const statusData = useMemo(() => {
    if (!data) return [];
    const distribution =
      activityMetric === "audio_minutes"
        ? data.distributions.audio_minutes_by_status
        : data.distributions.recordings_by_status;
    return Object.entries(distribution)
      .map(([name, value]) => ({ name: RECORDING_STATUS_LABEL[name] ?? name, value }))
      .sort((a, b) => b.value - a.value);
  }, [activityMetric, data]);

  const sourceData = useMemo(() => {
    if (!data) return [];
    const distribution =
      activityMetric === "audio_minutes"
        ? data.distributions.audio_minutes_by_source
        : data.distributions.recordings_by_source;
    return Object.entries(distribution)
      .map(([name, value]) => ({ name: RECORDING_SOURCE_LABEL[name] ?? name, value }))
      .sort((a, b) => b.value - a.value);
  }, [activityMetric, data]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (loading && !data) {
    return <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>;
  }

  if (!data) return null;

  const k = data.kpis;
  const showAudioMinutes = activityMetric === "audio_minutes";
  const recordingsValue = showAudioMinutes ? k.audio_minutes_window : k.recordings_window;
  const recordingsPrevious = showAudioMinutes
    ? k.audio_minutes_prev_window
    : k.recordings_prev_window;
  const dictationsValue = showAudioMinutes
    ? k.dictation_audio_minutes_window
    : k.dictations_window;
  const dictationsPrevious = showAudioMinutes
    ? k.dictation_audio_minutes_prev_window
    : k.dictations_prev_window;
  const conversationsValue = showAudioMinutes
    ? k.conversation_audio_minutes_window
    : k.conversations_window;
  const conversationsPrevious = showAudioMinutes
    ? k.conversation_audio_minutes_prev_window
    : k.conversations_prev_window;
  const recordingsDelta = deltaPercent(recordingsValue, recordingsPrevious);
  const dictationsDelta = deltaPercent(dictationsValue, dictationsPrevious);
  const conversationsDelta = deltaPercent(conversationsValue, conversationsPrevious);
  const isLeaf = data.top.level === "users";

  const sparkRecordings = data.daily.map((d) =>
    showAudioMinutes ? d.audio_minutes : d.recordings,
  );
  const sparkDictations = data.daily.map((d) =>
    showAudioMinutes ? d.dictation_audio_minutes : d.dictations,
  );
  const sparkConversations = data.daily.map((d) =>
    showAudioMinutes ? d.conversation_audio_minutes : d.conversations,
  );
  const sparkActive = data.daily.map((d) => d.active_users);
  const sparkFailedRecordings = data.daily.map((d) => d.failed_recordings);
  const topItems = showAudioMinutes ? data.top.audio_items : data.top.items;
  const activityValueFormatter = showAudioMinutes ? fmtMinutes : fmtNumber;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Breadcrumb
          scope={scope}
          scopeLabels={{
            org: data.scope.org?.name ?? null,
            department: data.scope.department?.name ?? null,
            association: data.scope.association?.name ?? null,
          }}
          rootLabel={isSuperuser ? "Globaal" : data.scope.org?.name ?? "Organisatie"}
          onJumpTo={jumpTo}
        />
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl range={range} onChange={setRange} />
          <SavedViewsMenu
            views={savedViews}
            onApply={handleApplyView}
            onSave={handleSaveCurrentView}
            onDelete={handleDeleteView}
          />
          <ExportMenu data={data} />
        </div>
      </div>

      <ScopeFilters
        isSuperuser={isSuperuser}
        scope={scope}
        organizations={organizations}
        departments={departments}
        loading={scopeOptionsLoading}
        activityMetric={activityMetric}
        onOrganizationChange={selectOrganization}
        onDepartmentChange={selectDepartment}
        onActivityMetricChange={setActivityMetric}
      />
      {scopeOptionsError ? (
        <p className="text-xs text-[var(--cs-rose)]">
          De ziekenhuis- en afdelingsfilters konden niet worden geladen: {scopeOptionsError}
        </p>
      ) : null}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
        <KpiCard label="Gebruikers" value={k.total_users} />
        <KpiCard label="Met opname vandaag" value={k.dau_today} spark={sparkActive} sparkColor={CS_COLORS.emerald} />
        <KpiCard label="Met opname (30d)" value={k.mau_30d} />
        <KpiCard
          label={showAudioMinutes ? "Audiominuten (periode)" : "Opnames (periode)"}
          value={recordingsValue}
          delta={recordingsDelta}
          format={showAudioMinutes ? "minutes" : "number"}
          spark={sparkRecordings}
          sparkColor={CS_COLORS.rose}
        />
        <KpiCard
          label={showAudioMinutes ? "Dictaatminuten (periode)" : "Dictaten (periode)"}
          value={dictationsValue}
          delta={dictationsDelta}
          format={showAudioMinutes ? "minutes" : "number"}
          spark={sparkDictations}
          sparkColor={CS_COLORS.navy}
        />
        <KpiCard
          label={showAudioMinutes ? "Gespreksminuten (periode)" : "Gesprekken (periode)"}
          value={conversationsValue}
          delta={conversationsDelta}
          format={showAudioMinutes ? "minutes" : "number"}
          spark={sparkConversations}
          sparkColor={CS_COLORS.emerald}
        />
        <KpiCard
          label="Mislukte opnames"
          value={k.failed_recordings_window}
          spark={sparkFailedRecordings}
          sparkColor={CS_COLORS.amber}
        />
        {isSuperuser ? (
          <KpiCard
            label="Kosten deze maand"
            value={k.cost_this_month_usd ?? 0}
            format="currency"
          />
        ) : (
          <KpiCard label="Uren bespaard" value={k.hours_saved_total} />
        )}
      </div>

      {/* Time series */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title={showAudioMinutes ? "Audiominuten per dag & cumulatief" : "Opnames per dag & cumulatief"}
        >
          {data.daily.length === 0 ? (
            <EmptyState message="Geen activiteit in deze periode." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={data.daily}>
                <CartesianGrid strokeDasharray={CHART_GRID.strokeDasharray} stroke={CHART_GRID.stroke} vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtTickDate} tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <YAxis yAxisId="left" allowDecimals={showAudioMinutes} tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <YAxis yAxisId="right" allowDecimals={showAudioMinutes} orientation="right" tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP.contentStyle}
                  labelStyle={CHART_TOOLTIP.labelStyle}
                  itemStyle={CHART_TOOLTIP.itemStyle}
                  labelFormatter={(label) => fmtTooltipDate(String(label))}
                  formatter={(v) => activityValueFormatter(Number(v))}
                />
                <Legend wrapperStyle={CHART_LEGEND.wrapperStyle} />
                <Bar
                  yAxisId="left"
                  dataKey={showAudioMinutes ? "dictation_audio_minutes" : "dictations"}
                  name={showAudioMinutes ? "Dictaatminuten per dag" : "Dictaten per dag"}
                  stackId="recordings"
                  fill={CS_COLORS.navy}
                  maxBarSize={28}
                />
                <Bar
                  yAxisId="left"
                  dataKey={showAudioMinutes ? "conversation_audio_minutes" : "conversations"}
                  name={showAudioMinutes ? "Gespreksminuten per dag" : "Gesprekken per dag"}
                  stackId="recordings"
                  fill={CS_COLORS.rose}
                  maxBarSize={28}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey={showAudioMinutes ? "audio_minutes_cumulative" : "recordings_cumulative"}
                  name={showAudioMinutes ? "Audiominuten cumulatief in periode" : "Opnames cumulatief in periode"}
                  stroke={CS_COLORS.emerald}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: CS_COLORS.emerald, stroke: "#fff", strokeWidth: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Gebruikers per dag & cumulatief">
          {data.daily.length === 0 ? (
            <EmptyState message="Geen activiteit in deze periode." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={data.daily}>
                <CartesianGrid strokeDasharray={CHART_GRID.strokeDasharray} stroke={CHART_GRID.stroke} vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtTickDate} tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <YAxis yAxisId="left" allowDecimals={false} tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <YAxis yAxisId="right" allowDecimals={false} orientation="right" tick={CHART_AXIS.tick} stroke={CHART_AXIS.stroke} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP.contentStyle}
                  labelStyle={CHART_TOOLTIP.labelStyle}
                  itemStyle={CHART_TOOLTIP.itemStyle}
                  labelFormatter={(label) => fmtTooltipDate(String(label))}
                  formatter={(v) => fmtNumber(Number(v))}
                />
                <Legend wrapperStyle={CHART_LEGEND.wrapperStyle} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="active_users"
                  name="Gebruikers met opname per dag"
                  fill={CS_COLORS.emerald}
                  stroke={CS_COLORS.emerald}
                  fillOpacity={0.30}
                />
                <Bar
                  yAxisId="left"
                  dataKey="new_users"
                  name="Nieuwe gebruikers per dag"
                  fill={CS_COLORS.amber}
                  maxBarSize={20}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="users_cumulative"
                  name="Gebruikers cumulatief"
                  stroke={CS_COLORS.navy}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: CS_COLORS.navy, stroke: "#fff", strokeWidth: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <ChartCard title={showAudioMinutes ? "Audiominuten per status" : "Opnames per status"}>
          <DistributionPie
            data={statusData}
            emptyMessage="Geen opnames in deze periode."
            valueFormatter={activityValueFormatter}
          />
        </ChartCard>
        <ChartCard title={showAudioMinutes ? "Audiominuten per bron" : "Opnames per bron"}>
          <DistributionPie
            data={sourceData}
            emptyMessage="Geen opnames in deze periode."
            valueFormatter={activityValueFormatter}
          />
        </ChartCard>
        <ChartCard title={TOP_LEVEL_LABEL[data.top.level]}>
          <TopList
            items={topItems}
            onDrill={isLeaf ? null : drillInto}
            metric={activityMetric}
          />
        </ChartCard>
      </div>

      {/* Top users — by recordings / by cost */}
      <div
        className={`grid grid-cols-1 gap-4 ${
          isSuperuser && data.top_users_by_cost ? "lg:grid-cols-2" : ""
        }`}
      >
        <ChartCard title={showAudioMinutes ? "Top gebruikers — audiominuten" : "Top gebruikers — opnames"}>
          <TopUsersBar
            items={(showAudioMinutes
              ? data.top_users_by_audio_minutes
              : data.top_users_by_recordings
            ).map((u) => ({
              id: u.id,
              label: u.label,
              value: "audio_minutes" in u ? u.audio_minutes : u.recordings,
            }))}
            metricKey={showAudioMinutes ? "audio_minutes" : "recordings"}
            metricLabel={showAudioMinutes ? "Audiominuten" : "Opnames"}
            color={CS_COLORS.navy}
            format={showAudioMinutes ? "minutes" : "number"}
            emptyMessage="Geen opnames in deze periode."
          />
        </ChartCard>
        {isSuperuser && data.top_users_by_cost ? (
          <ChartCard title="Top gebruikers — kosten">
            <TopUsersBar
              items={data.top_users_by_cost.map((u) => ({
                id: u.id,
                label: u.label,
                value: u.cost_usd,
              }))}
              metricKey="cost_usd"
              metricLabel="Kosten"
              color={CS_COLORS.rose}
              format="currency"
              emptyMessage="Geen kosten in deze periode."
            />
          </ChartCard>
        ) : null}
      </div>

      {/* Operational */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Recente fouten (24u)">
          {data.operational.recent_errors.length === 0 ? (
            <EmptyState message="Geen fouten in de laatste 24 uur." />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[rgba(10,52,96,0.10)]">
                  <th className="cs-caption pb-2 text-left">Foutsoort</th>
                  <th className="cs-caption pb-2 text-right">Aantal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {data.operational.recent_errors.map((err) => (
                  <tr key={err.error_type}>
                    <td className="cs-mono py-2 text-[11px] text-[var(--cs-navy)]">
                      {err.error_type}
                    </td>
                    <td className="cs-mono py-2 text-right text-[var(--cs-navy)]">
                      {fmtNumber(err.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
